/*
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { type AllMessages, type GetMessages, type LocaleOptions, TranslationStringStatus } from './types';
import { useEffect, useState } from 'react';
import { MissingLocaleError } from './errors';
import { getClosestLocalesForMessages } from './useIntlProvider';
import { getLocaleKey } from './getLocaleKey';

const timedPromise = <T>(promiseObject: Promise<T>, timeout: number): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(reject, timeout);
    promiseObject
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch(reject);
  });
};

export function useLocaleMessages(
  localizationResource: GetMessages | AllMessages | undefined,
  localeOptions: LocaleOptions,
): [TranslationStringStatus, AllMessages | undefined] {
  const isDynamicLoading = typeof localizationResource === 'function';
  const [messages, setMessages] = useState<AllMessages>();
  const [status, setStatus] = useState<TranslationStringStatus>(
    isDynamicLoading ? TranslationStringStatus.FETCHING : TranslationStringStatus.AVAILABLE,
  );
  const messagesLocale = getClosestLocalesForMessages(localeOptions).find(Boolean);

  if (!messagesLocale) {
    throw new MissingLocaleError();
  }
  useEffect(() => {
    if (isDynamicLoading) {
      const localeFileName = getLocaleKey(messagesLocale).toLowerCase();
      timedPromise(localizationResource(localeFileName), 2000)
        .then(({ default: localeMessages }) => {
          setMessages({ [getLocaleKey(messagesLocale)]: localeMessages });
          setStatus(TranslationStringStatus.AVAILABLE);
        })
        .catch(() => setStatus(TranslationStringStatus.ERRORED));
    }
  }, [messagesLocale, localizationResource]);
  return [status, messages];
}
